import { Clock } from "lucide-react";
import { useUnit } from "@/components/unit-provider";

interface HourlyTableProps {
  hourly: {
    dt: number;
    temp: number;
    feels_like: number;
    humidity: number;
    wind_speed: number;
    wind_deg: number;
    pop: number;
    weather: { icon: string; description: string }[];
  }[];
}

function dirArrow(deg: number) {
  // Wind FROM direction, arrow points where wind is going
  return `rotate(${deg + 180}deg)`;
}

export function HourlyTable({ hourly }: HourlyTableProps) {
  const { display } = useUnit();
  const rows = hourly.slice(0, 12);

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Clock className="h-4 w-4 text-primary" />
          Hourly Detail · Next 12h
        </h3>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">UTC offset local</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border/50 bg-muted/20">
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Time</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Cond</th>
              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Temp</th>
              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Feels</th>
              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Wind</th>
              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Hum</th>
              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Rain</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((h, i) => {
              const time = new Date(h.dt * 1000);
              const isNow = i === 0;
              return (
                <tr
                  key={h.dt}
                  className={`border-b border-border/30 last:border-0 hover:bg-muted/30 transition-colors ${isNow ? "bg-primary/5" : ""}`}
                >
                  <td className="px-3 py-2">
                    <span className={`font-mono text-xs ${isNow ? "text-primary font-bold" : ""}`}>
                      {isNow ? "Now" : time.toLocaleTimeString([], { hour: "numeric" }).replace(" ", "")}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <img
                      src={`/api/icon/${h.weather?.[0]?.icon || "01d"}.png`}
                      alt={h.weather?.[0]?.description || ""}
                      className="h-7 w-7"
                      title={h.weather?.[0]?.description}
                    />
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">
                    {display(h.temp)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-muted-foreground">
                    {display(h.feels_like)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1.5 justify-end">
                      <span
                        className="inline-block text-primary text-[10px]"
                        style={{ transform: dirArrow(h.wind_deg) }}
                      >
                        ↑
                      </span>
                      <span className="font-mono text-xs">{h.wind_speed.toFixed(1)}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs">
                    {h.humidity}%
                  </td>
                  <td className="px-3 py-2 text-right">
                    {h.pop > 0.1 ? (
                      <span className="font-mono text-xs text-blue-400">{Math.round(h.pop * 100)}%</span>
                    ) : (
                      <span className="text-muted-foreground/40">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
